import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import ticketCategoriesService from "../services/ticketCategoriesService";
import { Tags, Plus, Trash2 } from "lucide-react";

const CategoriasChamado = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const activeTenantName = useMemo(
    () => localStorage.getItem("active_tenant_name") || "",
    []
  );

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await ticketCategoriesService.list(true); // inclui inativas
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao carregar categorias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await ticketCategoriesService.create(name);
      setNewName("");
      setMessage(`Categoria "${name}" criada.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao criar categoria.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (cat) => {
    try {
      setError("");
      await ticketCategoriesService.update(cat.id, {
        name: cat.name,
        is_active: !cat.is_active,
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao atualizar categoria.");
    }
  };

  const rename = async (cat) => {
    const novo = window.prompt("Novo nome da categoria:", cat.name);
    if (novo == null) return;
    const name = novo.trim();
    if (!name || name === cat.name) return;
    try {
      setError("");
      await ticketCategoriesService.update(cat.id, { name, is_active: cat.is_active });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao renomear categoria.");
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Excluir a categoria "${cat.name}"?`)) return;
    try {
      setError("");
      await ticketCategoriesService.remove(cat.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao excluir categoria.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Categorias de Chamado</h1>
          <p className="text-gray-500">
            Categorias que aparecem no listbox ao editar um chamado
            {activeTenantName ? ` — ${activeTenantName}` : ""}.
          </p>
        </div>

        <div className="rounded-lg border bg-white p-4">
          <form onSubmit={handleAdd} className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">Nova categoria</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex.: Telefonia, Acesso e Senha..."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <Button type="submit" disabled={saving} className="gap-2">
              <Plus size={16} /> Adicionar
            </Button>
          </form>
        </div>

        {message && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">{message}</div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        <div className="rounded-lg border bg-white overflow-x-auto">
          {loading ? (
            <div className="p-6 text-gray-500">Carregando...</div>
          ) : categories.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Tags className="mx-auto mb-2 text-gray-300" size={28} />
              Nenhuma categoria cadastrada.
            </div>
          ) : (
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b text-left text-gray-600">
                  <th className="px-4 py-3 font-semibold">Categoria</th>
                  <th className="px-4 py-3 font-semibold w-28">Status</th>
                  <th className="px-4 py-3 font-semibold w-48 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{cat.name}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          cat.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {cat.is_active ? "Ativa" : "Inativa"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => rename(cat)}
                          className="h-auto px-2.5 py-1 text-xs text-gray-700"
                        >
                          Renomear
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => toggleActive(cat)}
                          className="h-auto px-2.5 py-1 text-xs text-gray-700"
                        >
                          {cat.is_active ? "Desativar" : "Ativar"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => remove(cat)}
                          aria-label="Excluir"
                          className="h-auto px-2.5 py-1 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CategoriasChamado;
